/**
 * 召回评测（`#recall-eval`，评测模式 › 召回评测，新增菜单项 D-32）——P1 召回第 5 步，新建（判定卡 12、线框 06；方案 v1.0 §五）。
 * 召回这一层单独评：逐轮金标单层回放（臂 基线／粗召回／排序 × 喂法 金标／串联 × 范围），看工具与指南的召回率、精准率，
 * 坏例下钻到候选表（选中和没选中的都列）。地址参数 `rr`（看哪次回放）、`rrb`（对比哪次），刷新与分享能还原。
 */
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Drawer } from "@/components/ui/drawer";
import { EmptyState, LoadingInline } from "@/components/ui/feedback";
import { Pagination } from "@/components/ui/pagination";
import { LabeledSelect } from "@/components/ui/select";
import { KpiCard } from "@/components/ui/stats";
import { Tabs } from "@/components/ui/tabs";
import {
	type RecallBadCase,
	type RecallCandidate,
	type RecallGuideTrace,
	type RecallReport,
	type RecallRunSummary,
	planErrorMessage,
	useRecallDp,
	useRecallEstimate,
	useRecallGold,
	useRecallProfiles,
	useRecallRun,
	useRecallRunCreate,
	useRecallRuns,
} from "@/lib/api/plan-lab";

import { FailState } from "../kit/fail-state";
import type { MetricId } from "../kit/metric-defs";
import { MetricTip } from "../kit/metric-tip";
import { PageJump } from "../kit/page-jump";
import { CellTags, type PlanColumn, PlanTable } from "../kit/plan-table";
import { Segmented } from "../kit/segmented";
import {
	ARM_LABEL,
	ENTRY_LABEL,
	FEED_LABEL,
	METRIC_LABEL,
	SPLIT_LABEL,
	fmtRate,
	labelOf,
} from "./recall-labels";

type View = "run" | "report";

export function RecallEvalTab({ runId, compareId }: { runId?: string; compareId?: string }) {
	const navigate = useNavigate();
	const [view, setView] = useState<View>(runId ? "report" : "run");
	const setSearch = (s: { rr?: string; rrb?: string }) =>
		navigate({ to: "/plan-lab", search: s, hash: "recall-eval", replace: true });
	return (
		<>
			<div data-testid="plan-block-tabs">
				<Tabs
					items={[
						{ key: "run", label: "发起" },
						{ key: "report", label: "报告" },
					]}
					active={view}
					onChange={setView}
				/>
			</div>
			{view === "run" ? (
				<LaunchView
					onOpen={(id) => {
						setSearch({ rr: id });
						setView("report");
					}}
				/>
			) : (
				<ReportView
					runId={runId}
					compareId={compareId}
					onPick={(rr, rrb) => setSearch({ rr, rrb })}
				/>
			)}
		</>
	);
}

function LaunchView({ onOpen }: { onOpen: (id: string) => void }) {
	const gold = useRecallGold();
	const profs = useRecallProfiles();
	const runs = useRecallRuns();
	const create = useRecallRunCreate();
	const [g, setG] = useState("");
	const [pv, setPv] = useState("");
	const [arm, setArm] = useState("jev");
	const [feed, setFeed] = useState("gold");
	const [split, setSplit] = useState("");
	useEffect(() => {
		const last = gold.data?.versions.at(-1);
		if (!g && last) setG(last);
	}, [g, gold.data]);
	useEffect(() => {
		if (!pv && profs.data) setPv(profs.data.active);
	}, [pv, profs.data]);
	const est = useRecallEstimate({ gold: g, arm, feed, split });
	const running = runs.data?.rows.some((r) => r.status === "running");
	const chainBad = feed === "chain" && arm === "baseline";
	const reason = !g
		? "没有可用的金标"
		: chainBad
			? "基线臂不看信号，串联喂入没有意义"
			: running
				? "有一次回放还在跑，跑完再发起"
				: undefined;

	return (
		<div className="space-y-4">
			<Card data-testid="plan-block-recall-launch">
				<CardContent className="space-y-3 py-4">
					<div className="flex flex-wrap items-center gap-3">
						<LabeledSelect
							label="金标"
							value={g}
							onChange={setG}
							options={(gold.data?.versions ?? []).map((v) => ({ value: v, label: v }))}
						/>
						<LabeledSelect
							label="画像版本"
							value={pv}
							onChange={setPv}
							options={(profs.data?.versions ?? []).map((v) => ({
								value: v.version,
								label: `${v.version}${v.active ? "（生效）" : ""}`,
							}))}
						/>
						<span className="text-xs text-text-muted">
							画像在 <PageJump to="recall" /> 管理
						</span>
						<LabeledSelect
							label="臂"
							value={arm}
							onChange={setArm}
							options={Object.entries(ARM_LABEL).map(([value, label]) => ({ value, label }))}
						/>
						<LabeledSelect
							label="喂法"
							value={feed}
							onChange={setFeed}
							options={Object.entries(FEED_LABEL).map(([value, label]) => ({ value, label }))}
						/>
						<LabeledSelect
							label="范围"
							value={split}
							onChange={setSplit}
							options={Object.entries(SPLIT_LABEL).map(([value, label]) => ({ value, label }))}
						/>
					</div>
					<div className="flex flex-wrap items-center gap-3 text-xs text-text-secondary">
						{est.data ? (
							<span data-testid="plan-recall-estimate">
								预估：{est.data.decision_points} 个决策点 · 模型调用 {est.data.llm_calls} 次 · 约{" "}
								{Math.ceil(est.data.est_seconds / 60)} 分钟 · 约 ${est.data.est_usd}
								{est.data.llm_calls === 0 ? "（不调模型，0 花费）" : ""}
							</span>
						) : est.isError ? (
							<span>预估失败：{planErrorMessage(est.error)}</span>
						) : null}
						<span className="ml-auto">
							<Button
								type="button"
								size="sm"
								disabled={!!reason || create.isPending}
								title={reason}
								data-testid="plan-recall-launch"
								onClick={() =>
									create.mutate(
										{ gold: g, profile_version: pv || undefined, arm, feed, split: split || null },
										{ onSuccess: (r) => onOpen(r.run_id) },
									)
								}
							>
								{create.isPending ? "发起中…" : "发起"}
							</Button>
						</span>
					</div>
					{create.isError ? (
						<p className="text-xs text-brand-danger">{planErrorMessage(create.error)}</p>
					) : null}
				</CardContent>
			</Card>
			<RunsTable runs={runs} onOpen={onOpen} />
		</div>
	);
}

function statusBadge(r: { status: string; progress?: { done: number; total: number } }) {
	if (r.status === "running")
		return (
			<Badge tone="info">
				进行中 {r.progress?.done ?? 0}/{r.progress?.total ?? 0}
			</Badge>
		);
	if (r.status === "failed") return <Badge tone="danger">失败</Badge>;
	return <Badge tone="success">完成</Badge>;
}

function RunsTable({
	runs,
	onOpen,
}: {
	runs: ReturnType<typeof useRecallRuns>;
	onOpen: (id: string) => void;
}) {
	const cols: PlanColumn<RecallRunSummary>[] = [
		{
			key: "id",
			title: "回放号",
			className: "w-28",
			render: (r) => <span className="font-mono">{r.run_id}</span>,
		},
		{
			key: "at",
			title: "时间",
			className: "w-36",
			render: (r) => r.created_at.slice(5, 16).replace("T", " "),
		},
		{
			key: "gold",
			title: "金标",
			render: (r) => <span className="font-mono text-xs">{r.gold}</span>,
		},
		{
			key: "pv",
			title: "画像",
			render: (r) => <span className="font-mono text-xs">{r.profile_version}</span>,
		},
		{ key: "arm", title: "臂", render: (r) => labelOf(ARM_LABEL, r.arm) },
		{ key: "feed", title: "喂法", render: (r) => labelOf(FEED_LABEL, r.feed) },
		{ key: "split", title: "范围", render: (r) => labelOf(SPLIT_LABEL, r.split ?? "") },
		{
			key: "tr",
			title: "工具召回",
			align: "right",
			render: (r) => <span className="font-mono">{fmtRate(r.metrics.tool_recall)}</span>,
		},
		{
			key: "tp",
			title: "精准率",
			align: "right",
			render: (r) => <span className="font-mono">{fmtRate(r.metrics.tool_precision)}</span>,
		},
		{
			key: "rh",
			title: "推荐档命中",
			align: "right",
			render: (r) => <span className="font-mono">{fmtRate(r.metrics.rec_hit)}</span>,
		},
		{
			key: "t1",
			title: "第 1 名",
			align: "right",
			render: (r) => <span className="font-mono">{fmtRate(r.metrics.top1)}</span>,
		},
		{
			key: "gr",
			title: "指南召回",
			align: "right",
			render: (r) => <span className="font-mono">{fmtRate(r.metrics.guide_recall)}</span>,
		},
		{ key: "st", title: "状态", render: statusBadge },
	];
	return (
		<Card data-testid="plan-block-recall-runs">
			<CardHeader>
				<CardTitle>历史回放</CardTitle>
			</CardHeader>
			<CardContent className="space-y-2">
				{runs.isLoading ? <LoadingInline /> : null}
				{runs.isError ? (
					<FailState
						reason={planErrorMessage(runs.error)}
						onRetry={() => runs.refetch()}
						retrying={runs.isFetching}
					/>
				) : null}
				{runs.data?.rows.length === 0 ? (
					<EmptyState
						title="还没有回放"
						desc="在上面选好金标、画像版本和臂，发起一次。粗召回和基线臂不调模型。"
					/>
				) : null}
				{runs.data?.rows.length ? (
					<>
						<PlanTable
							columns={cols}
							rows={runs.data.rows}
							rowKey={(r) => r.run_id}
							onRowClick={(r) => onOpen(r.run_id)}
							rowTestId="plan-recall-run-row"
						/>
						<p className="text-xs text-text-muted">共 {runs.data.total} 条 · 点一行看报告</p>
					</>
				) : null}
			</CardContent>
		</Card>
	);
}

const KPI_KEYS = [
	"tool_recall",
	"rec_hit",
	"tool_precision",
	"top1",
	"cannot_do_hit",
	"guide_recall",
	"guide_false_recall",
] as const;

function delta(a: number | null | undefined, b: number | null | undefined, lowerBetter = false) {
	if (a === null || a === undefined || b === null || b === undefined) return null;
	const d = a - b;
	if (Math.abs(d) < 0.0005) return <span className="text-text-muted">与对比持平</span>;
	const good = lowerBetter ? d < 0 : d > 0;
	return (
		<span className={good ? "text-brand-success" : "text-brand-danger"}>
			比对比 {d > 0 ? "+" : ""}
			{d.toFixed(3)}
		</span>
	);
}

function ReportView({
	runId,
	compareId,
	onPick,
}: {
	runId?: string;
	compareId?: string;
	onPick: (rr?: string, rrb?: string) => void;
}) {
	const runs = useRecallRuns();
	const run = useRecallRun(runId);
	const cmp = useRecallRun(compareId);
	const [dp, setDp] = useState<RecallBadCase | null>(null);
	const [kind, setKind] = useState("all");
	const options = (runs.data?.rows ?? []).filter((r) => r.status === "done" || r.run_id === runId);

	const picker = (
		<Card data-testid="plan-block-recall-pick">
			<CardContent className="flex flex-wrap items-center gap-3 py-3">
				<LabeledSelect
					label="回放"
					value={runId ?? ""}
					onChange={(v) => onPick(v || undefined, compareId)}
					options={[
						{ value: "", label: "选一次回放" },
						...options.map((r) => ({
							value: r.run_id,
							label: `${r.run_id} · ${labelOf(ARM_LABEL, r.arm)} · ${labelOf(FEED_LABEL, r.feed)}`,
						})),
					]}
				/>
				<LabeledSelect
					label="对比"
					value={compareId ?? ""}
					onChange={(v) => onPick(runId, v || undefined)}
					options={[
						{ value: "", label: "无" },
						...options
							.filter((r) => r.run_id !== runId)
							.map((r) => ({
								value: r.run_id,
								label: `${r.run_id} · ${labelOf(ARM_LABEL, r.arm)}`,
							})),
					]}
				/>
			</CardContent>
		</Card>
	);

	if (!runId)
		return (
			<div className="space-y-4">
				{picker}
				<Card>
					<CardContent>
						<EmptyState title="还没选回放" desc="在上面选一次回放，或去「发起」页签发起一次。" />
					</CardContent>
				</Card>
			</div>
		);
	if (run.isLoading)
		return (
			<div className="space-y-4">
				{picker}
				<LoadingInline />
			</div>
		);
	if (run.isError || !run.data)
		return (
			<div className="space-y-4">
				{picker}
				<FailState
					reason={run.error ? planErrorMessage(run.error) : "没有这次回放"}
					onRetry={() => run.refetch()}
					retrying={run.isFetching}
				/>
			</div>
		);
	const r = run.data;
	if (r.status !== "done" || !r.report)
		return (
			<div className="space-y-4">
				{picker}
				<Card data-testid="plan-block-recall-progress">
					<CardContent className="space-y-2 py-4 text-sm">
						<div className="flex items-center gap-2">
							{statusBadge(r)}{" "}
							<span className="text-text-secondary">
								{labelOf(ARM_LABEL, r.arm)} · {labelOf(FEED_LABEL, r.feed)} · {r.profile_version}
							</span>
						</div>
						{r.status === "running" ? (
							<LoadingInline text={`回放中… ${r.progress.done}/${r.progress.total}`} />
						) : null}
						{r.status === "failed" ? (
							<p className="text-xs text-brand-danger">失败原因：{r.error}</p>
						) : null}
					</CardContent>
				</Card>
			</div>
		);
	const rep = r.report;
	const c = cmp.data?.status === "done" ? cmp.data.report : undefined;
	return (
		<div className="space-y-4">
			{picker}
			<div
				className="grid gap-3 sm:grid-cols-4 lg:grid-cols-7"
				data-testid="plan-block-recall-kpis"
			>
				{KPI_KEYS.map((k) => (
					<KpiCard
						key={k}
						label={<MetricTip id={`recall.${k}` as MetricId}>{METRIC_LABEL[k]}</MetricTip>}
						value={k === "cannot_do_hit" ? cannotDo(rep) : fmtRate(rep[k])}
						hint={
							c
								? delta(rep[k], c[k], k === "guide_false_recall")
								: k === "tool_recall"
									? `前 ${rep.top_n}`
									: undefined
						}
					/>
				))}
			</div>
			{rep.avg_candidates !== undefined && rep.avg_candidates !== null ? (
				<p className="text-xs text-text-secondary" data-testid="plan-recall-avg">
					每件诉求平均候选 {rep.avg_candidates} 个 · 推荐档平均 {rep.avg_recommended ?? "—"}{" "}
					个（不含常备）
				</p>
			) : null}
			{rep.knowledge ? (
				<p className="text-xs text-text-secondary" data-testid="plan-recall-knowledge">
					知识推送（对知识推送金标 kn-v1.1，拿不准的不计）：
					<MetricTip id="recall.knowledge_push_recall">知识召回率</MetricTip>{" "}
					{fmtRate(rep.knowledge.push_recall)} ·{" "}
					<MetricTip id="recall.knowledge_false_push">知识误推率</MetricTip>{" "}
					{fmtRate(rep.knowledge.false_push)} ·
					<MetricTip id="recall.knowledge_avg_pushed">每件诉求平均推</MetricTip>{" "}
					{rep.knowledge.avg_pushed ?? "—"} 条
					{c?.knowledge
						? `（对比：${fmtRate(c.knowledge.push_recall)} ／ ${fmtRate(c.knowledge.false_push)}）`
						: ""}
				</p>
			) : null}
			{rep.signal ? (
				<p className="text-xs text-text-secondary" data-testid="plan-recall-signal">
					信号层：<MetricTip id="recall.signal_count_match">诉求件数对上</MetricTip>{" "}
					{rep.signal.count_match}/{rep.signal.dp} ·{" "}
					<MetricTip id="recall.signal_multi_hit">多诉求报全</MetricTip> {rep.signal.multi_hit}/
					{rep.signal.multi_gold}
				</p>
			) : null}
			<SplitTable rep={rep} />
			<ConfusionTable rep={rep} />
			<BadCaseTable rep={rep} kind={kind} setKind={setKind} onOpen={setDp} />
			<DpDrawer runId={runId} bad={dp} onClose={() => setDp(null)} />
		</div>
	);
}

function cannotDo(rep: RecallReport) {
	return rep.cannot_do_hit === null
		? "—"
		: `${fmtRate(rep.cannot_do_hit)}（误报 ${rep.cannot_do_false}）`;
}

function SplitTable({ rep }: { rep: RecallReport }) {
	const rows = Object.entries(rep.by_split).map(([k, v]) => ({ k, ...v }));
	const cols: PlanColumn<(typeof rows)[number]>[] = [
		{ key: "k", title: "范围", className: "w-24", render: (x) => labelOf(SPLIT_LABEL, x.k) },
		{ key: "n", title: "决策点", align: "right", render: (x) => x.decision_points },
		...KPI_KEYS.map((k) => ({
			key: k,
			title: <MetricTip id={`recall.${k}` as MetricId}>{METRIC_LABEL[k]}</MetricTip>,
			align: "right" as const,
			render: (x: (typeof rows)[number]) => <span className="font-mono">{fmtRate(x[k])}</span>,
		})),
	];
	return (
		<Card data-testid="plan-block-recall-split">
			<CardHeader>
				<CardTitle>开发集 ／ 留出集（改画像只看开发集，留出集只评不改）</CardTitle>
			</CardHeader>
			<CardContent>
				<PlanTable columns={cols} rows={rows} rowKey={(x) => x.k} density="compact" />
			</CardContent>
		</Card>
	);
}

function ConfusionTable({ rep }: { rep: RecallReport }) {
	const cols: PlanColumn<RecallReport["confusion"][number]>[] = [
		{ key: "g", title: "金标该用", render: (x) => <span className="font-mono">{x.gold}</span> },
		{ key: "o", title: "实际排第一", render: (x) => <span className="font-mono">{x.got}</span> },
		{ key: "n", title: "次数", align: "right", render: (x) => x.n },
	];
	return (
		<Card data-testid="plan-block-recall-confusion">
			<CardHeader>
				<CardTitle>易混对</CardTitle>
			</CardHeader>
			<CardContent className="space-y-2">
				{rep.confusion.length ? (
					<>
						<PlanTable
							columns={cols}
							rows={rep.confusion}
							rowKey={(x) => `${x.gold}>${x.got}`}
							density="compact"
						/>
						<p className="text-xs text-text-muted">共 {rep.confusion.length} 条</p>
					</>
				) : (
					<EmptyState title="没有排错的" desc="第 1 名全部命中，或这次回放的臂不排序。" />
				)}
			</CardContent>
		</Card>
	);
}

const PAGE = 20;

function BadCaseTable({
	rep,
	kind,
	setKind,
	onOpen,
}: {
	rep: RecallReport;
	kind: string;
	setKind: (k: string) => void;
	onOpen: (b: RecallBadCase) => void;
}) {
	const [page, setPage] = useState(0);
	const rows = useMemo(
		() => rep.bad_cases.filter((b) => kind === "all" || (kind === "miss" ? b.miss : !b.miss)),
		[rep.bad_cases, kind],
	);
	// biome-ignore lint/correctness/useExhaustiveDependencies: 换坏例类型时回到第一页，依赖是有意的
	useEffect(() => setPage(0), [kind]);
	const pages = Math.max(1, Math.ceil(rows.length / PAGE));
	const cols: PlanColumn<RecallBadCase>[] = [
		{
			key: "dp",
			title: "决策点",
			className: "w-36",
			render: (b) => <span className="font-mono">{b.dp_id}</span>,
		},
		{
			key: "goal",
			title: "诉求 goal",
			className: "max-w-[280px]",
			render: (b) => (
				<span className="block truncate" title={b.goal ?? ""}>
					{b.goal}
				</span>
			),
		},
		{ key: "acc", title: "该用", render: (b) => <CellTags mono items={b.acceptable} /> },
		{
			key: "top",
			title: "实际前 3",
			render: (b) => <CellTags mono items={b.top.slice(0, 3)} max={3} />,
		},
		{
			key: "kind",
			title: "类型",
			render: (b) =>
				b.miss ? <Badge tone="danger">漏召回</Badge> : <Badge tone="warning">第 1 名错</Badge>,
		},
	];
	return (
		<Card data-testid="plan-block-recall-bad">
			<CardHeader>
				<CardTitle>坏例</CardTitle>
			</CardHeader>
			<CardContent className="space-y-3">
				<LabeledSelect
					label="类型"
					value={kind}
					onChange={setKind}
					options={[
						{ value: "all", label: "全部" },
						{ value: "miss", label: "漏召回" },
						{ value: "top1", label: "第 1 名错" },
					]}
				/>
				{rows.length ? (
					<>
						<PlanTable
							columns={cols}
							rows={rows.slice(page * PAGE, page * PAGE + PAGE)}
							rowKey={(b) => `${b.dp_id}|${b.gold_item}`}
							onRowClick={onOpen}
							rowTestId="plan-recall-bad-row"
						/>
						<div className="flex items-center justify-between gap-3">
							<span className="text-xs text-text-muted">共 {rows.length} 条</span>
							<Pagination page={page} pageCount={pages} onChange={setPage} />
						</div>
					</>
				) : (
					<EmptyState title="没有坏例" desc="这个类型下没有错的。" />
				)}
			</CardContent>
		</Card>
	);
}

type Seg = "tools" | "guides" | "raw";

function DpDrawer({
	runId,
	bad,
	onClose,
}: { runId?: string; bad: RecallBadCase | null; onClose: () => void }) {
	const q = useRecallDp(runId, bad?.dp_id);
	const [seg, setSeg] = useState<Seg>("tools");
	const d = q.data;
	return (
		<Drawer
			open={!!bad}
			onClose={onClose}
			width="min(1080px,95vw)"
			testId="plan-recall-dp-drawer"
			title={<span className="font-mono">{bad?.dp_id}</span>}
			description={d?.message ? `客户：${d.message}` : undefined}
			badges={d?.split ? <Badge tone="muted">{labelOf(SPLIT_LABEL, d.split)}</Badge> : undefined}
		>
			<div className="space-y-4">
				<Segmented
					options={[
						{ value: "tools", label: "工具候选" },
						{ value: "guides", label: "指南" },
						{ value: "raw", label: "判定原文" },
					]}
					value={seg}
					onChange={setSeg}
				/>
				{q.isLoading ? <LoadingInline /> : null}
				{q.isError ? (
					<FailState
						reason={planErrorMessage(q.error)}
						onRetry={() => q.refetch()}
						retrying={q.isFetching}
					/>
				) : null}
				{d && seg === "tools" ? (
					<div className="space-y-4">
						{d.gold_demands.map((gd) => {
							const got = d.result?.demands?.find((x) => x.gold_item === gd.gold_item);
							return (
								<div key={gd.gold_item} className="space-y-2">
									<div className="text-sm">
										<span className="text-text-muted">诉求：</span>
										{gd.goal}
										<span className="ml-3 text-xs text-text-muted">金标可接受：</span>
										<span className="font-mono text-xs">
											{gd.acceptable_tools.join("、") || "—"}
										</span>
										{gd.cannot_do ? (
											<Badge tone="warning" className="ml-2">
												金标：无能办的
											</Badge>
										) : null}
										{got?.conclusion ? (
											<Badge tone="muted" className="ml-2">
												本次结论：{got.conclusion}
											</Badge>
										) : null}
									</div>
									<CandidateTable rows={got?.candidates ?? []} acceptable={gd.acceptable_tools} />
								</div>
							);
						})}
						<p className="text-xs text-text-muted">要改画像去「应答配置 › 召回」。</p>
					</div>
				) : null}
				{d && seg === "guides" ? (
					<div className="space-y-3">
						<GuideCompare
							gold={d.gold_guides}
							got={d.result?.guides ?? []}
							trace={d.result?.guide_trace}
						/>
						{d.gold_knowledge && Object.keys(d.gold_knowledge).length ? (
							<KnowledgeCompare gold={d.gold_knowledge} got={d.result?.knowledge ?? {}} />
						) : null}
					</div>
				) : null}
				{d && seg === "raw" ? (
					<pre className="whitespace-pre-wrap rounded-md border border-surface-border bg-surface-muted/40 p-3 font-mono text-xs">
						{JSON.stringify(
							{ data_states: d.result?.data_states, judged: d.result?.judged },
							null,
							2,
						)}
					</pre>
				) : null}
			</div>
		</Drawer>
	);
}

function fmtNum(v: number | null | undefined): string {
	return v === null || v === undefined ? "—" : String(Math.round(v * 100) / 100);
}

function CandidateTable({ rows, acceptable }: { rows: RecallCandidate[]; acceptable: string[] }) {
	const cols: PlanColumn<RecallCandidate>[] = [
		{ key: "rank", title: "名次", className: "w-12", render: (c) => c.rank ?? "—" },
		{
			key: "tool",
			title: "工具",
			render: (c) => (
				<span className="font-mono">
					{c.tool_id}
					{acceptable.includes(c.tool_id) ? (
						<Badge tone="success" className="ml-1.5">
							金标
						</Badge>
					) : null}
				</span>
			),
		},
		{
			key: "routes",
			title: "进来方式",
			render: (c) =>
				c.routes ? (
					<CellTags items={c.routes} max={3} />
				) : (
					<CellTags
						items={[
							labelOf(ENTRY_LABEL, c.entry ?? ""),
							...(c.intent_tag ? ["意图标签命中"] : []),
							...(c.prerequisite_of?.length ? [`${c.prerequisite_of.join("、")} 的前置`] : []),
						].filter(Boolean)}
						max={3}
					/>
				),
		},
		{
			key: "rel",
			title: "相关度",
			align: "right",
			// 旧画像（四路口径）没有相关度，显示适配判断（是 ／ 否 ／ 拿不准）
			render: (c) => (
				<span className="font-mono">
					{c.relevance !== undefined ? fmtNum(c.relevance) : (c.fit ?? "—")}
				</span>
			),
		},
		{
			key: "bonus",
			title: "意图加分",
			align: "right",
			render: (c) => (
				<span className="font-mono">{c.intent_bonus ? `+${c.intent_bonus}` : "—"}</span>
			),
		},
		{
			key: "score",
			title: "最终分",
			align: "right",
			render: (c) => <span className="font-mono">{fmtNum(c.score)}</span>,
		},
		{
			key: "reason",
			title: "原因码",
			render: (c) => <span className="font-mono text-xs">{c.reason ?? "—"}</span>,
		},
		{
			key: "tier",
			title: "档位",
			render: (c) =>
				c.tier ? (
					<Badge tone={c.tier === "推荐" ? "success" : c.tier === "可选" ? "info" : "muted"}>
						{c.tier}
					</Badge>
				) : (
					"—"
				),
		},
		{
			key: "why",
			title: "为什么在这档",
			className: "max-w-[260px]",
			render: (c) => (
				<span className="block truncate text-text-secondary" title={c.tier_why ?? c.why ?? ""}>
					{c.tier_why ?? c.why ?? "—"}
				</span>
			),
		},
	];
	return rows.length ? (
		<PlanTable columns={cols} rows={rows} rowKey={(c) => c.tool_id} density="compact" />
	) : (
		<EmptyState title="没有候选" desc="这件诉求一个候选都没有召回。" />
	);
}

function GuideCompare({
	gold,
	got,
	trace,
}: {
	gold: Record<string, string>;
	got: string[];
	trace?: Record<string, RecallGuideTrace>;
}) {
	const ids = Array.from(new Set([...Object.keys(gold), ...got])).sort();
	// 画像 pv5 起：每条进来 ／ 没进来的原因（多件诉求时取进来的那件，都没进来取第一件）
	const why = (id: string) => {
		const ts = Object.values(trace ?? {});
		const hit = ts.flatMap((t) => t.in).find((h) => h.guide_id === id);
		return hit?.why ?? ts.flatMap((t) => t.out).find((h) => h.guide_id === id)?.why ?? "";
	};
	const rows = ids.map((id) => ({
		id,
		label: gold[id],
		recalled: got.includes(id),
		why: why(id),
	}));
	const cols: PlanColumn<(typeof rows)[number]>[] = [
		{ key: "id", title: "指南", render: (x) => <span className="font-mono">{x.id}</span> },
		{
			key: "gold",
			title: "金标",
			render: (x) =>
				x.label === "recall" ? "应召回" : x.label === "not_recall" ? "不应召回" : "未标",
		},
		{ key: "got", title: "本次", render: (x) => (x.recalled ? "召回了" : "没召回") },
		{
			key: "ok",
			title: "对错",
			render: (x) =>
				!x.label ? (
					"—"
				) : (x.label === "recall") === x.recalled ? (
					<Badge tone="success">对</Badge>
				) : (
					<Badge tone="danger">{x.recalled ? "误召回" : "漏召回"}</Badge>
				),
		},
		...(trace
			? [
					{
						key: "why",
						title: "为什么进来 ／ 没进来",
						className: "max-w-[280px]",
						render: (x: (typeof rows)[number]) => (
							<span className="block truncate text-text-secondary" title={x.why}>
								{x.why || "—"}
							</span>
						),
					},
				]
			: []),
	];
	return <PlanTable columns={cols} rows={rows} rowKey={(x) => x.id} density="compact" />;
}

type KnLabel = "push" | "not_push" | "unsure";

/** 知识推送对金标（批 4，kn-v1.1）：每件诉求逐条看该推 ／ 不该推 ／ 拿不准与本次推没推 */
function KnowledgeCompare({
	gold,
	got,
}: {
	gold: Record<string, Record<string, KnLabel>>;
	got: Record<string, string[]>;
}) {
	const rows = Object.entries(gold).flatMap(([gi, labels]) =>
		Object.entries(labels)
			.filter(([id, lab]) => lab !== "not_push" || (got[gi] ?? []).includes(id))
			.map(([id, lab]) => ({
				key: `${gi}:${id}`,
				gi,
				id,
				lab,
				pushed: (got[gi] ?? []).includes(id),
			})),
	);
	const cols: PlanColumn<(typeof rows)[number]>[] = [
		{
			key: "gi",
			title: "诉求",
			className: "w-14",
			render: (x) => <span className="font-mono">{x.gi}</span>,
		},
		{ key: "id", title: "知识条目", render: (x) => <span className="font-mono">{x.id}</span> },
		{
			key: "lab",
			title: "金标",
			render: (x) =>
				x.lab === "push" ? "该推" : x.lab === "not_push" ? "不该推" : "拿不准（不计）",
		},
		{ key: "got", title: "本次", render: (x) => (x.pushed ? "推了" : "没推") },
		{
			key: "ok",
			title: "对错",
			render: (x) =>
				x.lab === "unsure" ? (
					"—"
				) : (x.lab === "push") === x.pushed ? (
					<Badge tone="success">对</Badge>
				) : (
					<Badge tone="danger">{x.pushed ? "误推" : "漏推"}</Badge>
				),
		},
	];
	return (
		<div className="space-y-1" data-testid="plan-recall-knowledge-compare">
			<div className="text-xs text-text-muted">
				知识推送（只列该推的、拿不准的和推错的；其余不该推也没推）
			</div>
			<PlanTable columns={cols} rows={rows} rowKey={(x) => x.key} density="compact" />
		</div>
	);
}
